import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, ChevronLeft, ChevronRight, Volume2, Video } from "lucide-react";
import api from "@/lib/api";
import FmsPoseCapture from "@/components/FmsPoseCapture";

/**
 * FmsGuidedTest
 * ─────────────
 * 7-screen Functional Movement Screen wizard. Each movement is
 * graded 0-3 per the published rubric:
 *   3 = perfect performance, no compensation
 *   2 = completes with compensation
 *   1 = unable to complete the pattern
 *   0 = pain anywhere during the movement (automatic 0)
 *
 * On submit, POSTs to /api/screening/fms which writes the 7 sub-scores +
 * fms_total to player_profiles. Total < 14 = injury-risk band.
 *
 * Mode prop:
 *   • "self"   — solo athlete grades themselves (sees a mirror cue)
 *   • "coach"  — coach grades a roster athlete (must pass playerUserId)
 *   • "parent" — parent administers for a solo player (playerUserId optional;
 *                falls through to self if the auth principal IS the player)
 */
const SCREENS = [
  {
    key: "deep_squat",
    title: "Deep Squat",
    cue: "Hold a dowel overhead with arms straight. Squat as deep as you can with heels flat and dowel above the head.",
    voice: "Hold a dowel overhead with arms straight. Squat as deep as you can with heels flat and the dowel above your head.",
    rubric: ["Pain", "Cannot reach parallel even with heels elevated", "Completes with heels elevated or some compensation", "Perfect — thighs below parallel, dowel stays overhead, heels flat"],
  },
  {
    key: "hurdle_step",
    title: "Hurdle Step",
    cue: "Stand tall, place a dowel across the shoulders. Step over a hurdle at tibial-tuberosity height without losing balance.",
    voice: "Stand tall, place a dowel across your shoulders. Step over a hurdle at knee height without losing balance.",
    rubric: ["Pain", "Loses balance or cannot step over without contact", "Completes with compensation in spine or stance leg", "Perfect — both legs clear without compensation"],
  },
  {
    key: "inline_lunge",
    title: "Inline Lunge",
    cue: "Hold a dowel vertically on the back. Step into a tandem stance and lunge until the back knee touches the board.",
    voice: "Hold a dowel vertically on your back. Step into a tandem stance and lunge until your back knee touches the board.",
    rubric: ["Pain", "Loses balance, dowel separates from spine", "Completes with compensation or slight loss of balance", "Perfect — both knees touch, dowel stays in contact with spine"],
  },
  {
    key: "shoulder_mobility",
    title: "Shoulder Mobility",
    cue: "Make fists with thumbs tucked. Reach one fist over the shoulder and the other up the back. Measure the fist-to-fist distance.",
    voice: "Make fists with thumbs tucked. Reach one fist over the shoulder and the other up the back, then measure the distance between them.",
    rubric: ["Pain (or positive clearing test)", "Fists more than 1.5 hand-lengths apart", "Fists within 1.5 hand-lengths", "Fists within 1 hand-length"],
  },
  {
    key: "aslr",
    title: "Active Straight-Leg Raise",
    cue: "Lie supine, knees straight, toes pointed up. Raise one leg as high as possible while the other stays flat.",
    voice: "Lie on your back with knees straight and toes pointed up. Raise one leg as high as possible while the other stays flat on the floor.",
    rubric: ["Pain", "Ankle below the down-leg mid-thigh line", "Ankle between mid-thigh and the kneecap line", "Ankle clears the kneecap of the down leg"],
  },
  {
    key: "trunk_stability_pushup",
    title: "Trunk Stability Push-Up",
    cue: "From a push-up position with thumbs at forehead (men) / chin (women), press up with body rising as one unit.",
    voice: "From a push-up position with thumbs at your forehead, press up with your body rising as a single unit.",
    rubric: ["Pain (or positive clearing test)", "Cannot complete a rep at the modified hand position", "Completes a rep at the modified hand position", "Completes a rep at the full hand position"],
  },
  {
    key: "rotary_stability",
    title: "Rotary Stability",
    cue: "On hands and knees, simultaneously extend the right arm and right leg, then touch elbow to knee under the body.",
    voice: "On hands and knees, simultaneously extend the right arm and right leg, then touch elbow to knee under your body.",
    rubric: ["Pain (or positive clearing test)", "Cannot complete a diagonal pattern", "Completes a diagonal (opposite arm/leg) pattern", "Perfect — completes the unilateral (same-side) pattern"],
  },
];

export default function FmsGuidedTest({ mode = "self", playerUserId = null, onComplete, onCancel, ttsEnabled = true }) {
  const [idx, setIdx] = useState(0);
  const [scores, setScores] = useState({});
  const [busy, setBusy] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [evidenceByKey, setEvidenceByKey] = useState({});

  const screen = SCREENS[idx];
  const isLast = idx === SCREENS.length - 1;
  const allDone = SCREENS.every((s) => scores[s.key] !== undefined);

  const setScore = (s) => setScores({ ...scores, [screen.key]: s });
  const next = () => { setShowCamera(false); setIdx((i) => Math.min(SCREENS.length - 1, i + 1)); };
  const prev = () => { setShowCamera(false); setIdx((i) => Math.max(0, i - 1)); };

  // Movements that can be auto-graded via markerless video.
  // shoulder_mobility intentionally excluded — it needs a tape measure, not video.
  const POSE_CAPTURABLE = new Set(["deep_squat", "hurdle_step", "inline_lunge", "aslr", "trunk_stability_pushup", "rotary_stability"]);
  const canCapture = POSE_CAPTURABLE.has(screen.key);

  const onPoseScore = (autoScore, evidence) => {
    if (autoScore !== null && autoScore !== undefined) {
      setScore(autoScore);
      setEvidenceByKey({ ...evidenceByKey, [screen.key]: evidence });
    }
    setShowCamera(false);
  };

  const playCue = async () => {
    if (!ttsEnabled) return;
    try {
      const res = await api.post("/tts/speak", { text: screen.voice });
      if (res.data?.audio_b64) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio_b64}`);
        audio.play().catch(() => {});
      }
    } catch { /* TTS is best-effort */ }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...scores, method: mode };
      if (playerUserId) payload.player_user_id = playerUserId;
      if (Object.keys(evidenceByKey).length) payload.notes = JSON.stringify({ pose_evidence: evidenceByKey });
      const { data } = await api.post("/screening/fms", payload);
      onComplete?.(data);
    } finally {
      setBusy(false);
    }
  };

  // Done screen
  if (allDone && idx === SCREENS.length - 1 && scores[screen.key] !== undefined) {
    const total = Object.values(scores).reduce((a, b) => a + b, 0);
    const atRisk = total < 14;
    return (
      <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="fms-review">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Review & Save</div>
        <div className="font-display font-black text-3xl text-white">FMS Total: {total} / 21</div>
        <div className={`mt-1 text-sm font-bold ${atRisk ? "text-amber-400" : "text-emerald-400"}`}>
          {atRisk ? "Below 14 — increased injury-risk band" : "14 or above — within healthy band"}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-5">
          {SCREENS.map((s) => (
            <div key={s.key} className="rounded bg-black/30 px-3 py-2 flex items-center justify-between text-xs">
              <span className="text-white/70">{s.title}</span>
              <span className="text-white font-bold tabular-nums">{scores[s.key]}/3</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-6">
          <Button onClick={() => { setIdx(0); }} variant="outline" className="border-white/20 text-white" data-testid="fms-edit">
            Edit a score
          </Button>
          <Button onClick={submit} disabled={busy} className="flex-1" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="fms-submit">
            <CheckCircle2 className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save & Continue"}
          </Button>
        </div>
      </Card>
    );
  }

  const current = scores[screen.key];

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="fms-step">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Movement {idx + 1} of {SCREENS.length}</div>
          <div className="text-xl font-display font-black text-white mt-1">{screen.title}</div>
        </div>
        <button onClick={playCue} className="text-white/60 hover:text-white" data-testid="fms-voice-cue">
          <Volume2 className="h-5 w-5" />
        </button>
      </div>
      <p className="text-sm text-white/75 leading-relaxed mb-4">{screen.cue}</p>

      {/* Markerless video capture — optional auto-grade */}
      {canCapture && !showCamera && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 mb-5 flex items-center justify-between gap-3" data-testid="fms-capture-cta">
          <div>
            <div className="text-xs font-bold text-cyan-200">Record yourself — auto-grade</div>
            <div className="text-[11px] text-white/55 mt-0.5">On-device pose analysis suggests a score. Coach can override.</div>
          </div>
          <Button size="sm" variant="outline" className="border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10"
                  onClick={() => setShowCamera(true)} data-testid="fms-open-camera">
            <Video className="h-4 w-4 mr-1.5" /> Record
          </Button>
        </div>
      )}
      {showCamera && (
        <div className="mb-5">
          <FmsPoseCapture movement={screen.key} onScore={onPoseScore} onCancel={() => setShowCamera(false)} />
        </div>
      )}

      <div className="space-y-2 mb-5">
        {screen.rubric.map((label, score) => (
          <button key={score} onClick={() => setScore(score)} data-testid={`fms-score-${screen.key}-${score}`}
            className={`w-full text-left rounded-lg border px-4 py-3 transition-all
                        ${current === score
                          ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/10"
                          : "border-white/10 bg-black/30 hover:border-white/30"}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-bold tabular-nums" style={{ color: current === score ? "var(--brand-secondary)" : "#fff" }}>
                {score}/3
              </div>
              <div className="text-xs text-white/70 flex-1">{label}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Button onClick={prev} disabled={idx === 0} variant="outline" className="border-white/20 text-white" data-testid="fms-prev">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button onClick={next} disabled={current === undefined} className="flex-1"
                style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="fms-next">
          {isLast ? "Review" : "Next"} <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <button onClick={onCancel} className="mt-4 text-xs text-white/40 hover:text-white/70 w-full" data-testid="fms-cancel">
        Save & exit (you can resume later)
      </button>
    </Card>
  );
}
